import type { MouseEvent } from 'react';

import LocationIconSM from '@skyscanner/backpack-web/bpk-component-icon/sm/location';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';

import DescriptionPanel from './DescriptionPanel';
import Maps from './Maps';
import PoiCard from './PoiCard';

import type { PoiDataState } from '@hotels-hotel-detail/data-access/types';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { HotelContent } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId, Nullable } from '@shared/types/utils';

import STYLES from './DetailedLocation.module.scss';

import { withPoiDataState } from '@hotels-hotel-detail/data-access/contexts/PoiDataContext';
import SectionHeader from '@hotels-hotel-detail/ui/SectionHeader';
import {
  getReviewScoreKey,
  getLocationReviewSummary,
} from '@hotels-hotel-detail/utils/hotel-content';
import {
  getPoiCards,
  isSinglePoi,
} from '@hotels-hotel-detail/utils/hotel-poi-data';
import {
  TRANSPORT,
  AIRPORT,
  MAPPING_MAP_CLICK_TYPE,
} from '@hotels-hotel-detail/utils/location-constants';
import { buildAdditionalInfoNearbyMap } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import { HASH_FULL_MAP } from '@shared/constants/hash-routes';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import { withRouterLink } from '@shared/ui/common/RouteLink';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  CLICK_TYPE,
} from '@shared/utils-frontend/mini-events/constants';

const RouterPoiCard = withRouterLink(PoiCard, HASH_FULL_MAP);

type Props = {
  i18n: I18nService;
  poiDataState: PoiDataState;
  miniEventTracker: MiniEventTracker;
  uBFFHotel: HotelContent;
};

const DetailedLocation = ({
  i18n,
  miniEventTracker,
  poiDataState: {
    airport = [],
    nearbyPoi = [],
    onPoiMarkerClick = () => {},
    onTabClick = () => {},
    transportWithoutAirport = [],
  },
  uBFFHotel,
}: Props) => {
  const { distanceReference, hotelId, location, reviewSummary } = uBFFHotel;

  const locationReviewSummary = getLocationReviewSummary(reviewSummary);
  const { score: reviewSummaryScore } = locationReviewSummary ?? {};
  const title = i18n.translate(getReviewScoreKey(reviewSummaryScore));

  const isSingle = isSinglePoi({ nearbyPoi, transportWithoutAirport, airport });

  const poiCards = getPoiCards({
    i18n,
    nearbyPoi,
    transportWithoutAirport,
    airport,
  });

  const { distanceDescription: distanceText, entity } = distanceReference ?? {};

  const trackClick = (
    mapClickType: string,
    selectedPoi: Nullable<{
      entityId: EntityId;
      linearDistance: number;
    }>,
  ) => {
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.DETAIL_PAGE_NEARBY_MAP_CLICK,
      buildAdditionalInfoNearbyMap({
        isMapFullView: false,
        hotelId: parseInt(hotelId, 10),
        clickType: CLICK_TYPE[MAPPING_MAP_CLICK_TYPE[mapClickType]],
        poiInfo: selectedPoi
          ? {
              poi_id: parseInt(selectedPoi.entityId, 10),
              distance_in_meters: selectedPoi.linearDistance,
            }
          : null,
      }),
    );
  };

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
      {(isMobile: boolean) => (
        <>
          <div className={STYLES.DetailedLocation__header}>
            <SectionHeader title={title} />
            {reviewSummaryScore && (
              <BpkText
                textStyle={TEXT_STYLES.xxxl}
                className={STYLES.DetailedLocation__score}
                color={TEXT_COLORS.textPrimary}
              >
                {i18n.formatNumber(reviewSummaryScore)}
              </BpkText>
            )}
          </div>

          {!isMobile && (
            <div className={STYLES.DetailedLocation__addressAndDistance}>
              {location.address && (
                <>
                  <div
                    className={STYLES.DetailedLocation__addressIcon}
                    /* without a separate div, the icon will shrink to a smaller size when the text fills up all the horizontal space  */
                  >
                    <LocationIconSM />
                  </div>
                  <BpkText tagName="p">
                    <BpkText tagName="span">{location.address}</BpkText>
                    {distanceText && (
                      <>
                        <BpkText
                          tagName="span"
                          className={STYLES.DetailedLocation__distanceText}
                        >
                          |
                        </BpkText>
                        <BpkText
                          tagName="span"
                          className={STYLES.DetailedLocation__distanceText}
                          data-test-id="distance-from"
                        >
                          {distanceText}
                        </BpkText>
                      </>
                    )}
                  </BpkText>
                </>
              )}
            </div>
          )}

          <Maps
            className={STYLES['DetailedLocation__map--height']}
            trackClick={trackClick}
            isMobile={isMobile}
            hotelId={hotelId}
          />

          {!isMobile && poiCards.length > 0 && (
            <div className={STYLES.DetailedLocation__cardContainer}>
              {poiCards.map(({ data, tab, title: poiTitle }) => (
                <RouterPoiCard
                  key={poiTitle}
                  onPoiMarkerClick={onPoiMarkerClick}
                  onClick={(
                    _e: MouseEvent,
                    selectedPoi: Nullable<{
                      entityId: EntityId;
                      linearDistance: number;
                    }>,
                  ) => {
                    trackClick(tab, selectedPoi);
                    onTabClick(tab === AIRPORT ? TRANSPORT : tab);
                  }}
                  isSingle={isSingle}
                  title={poiTitle}
                  poiData={data}
                  hotelId={hotelId}
                  poiTab={tab}
                />
              ))}
            </div>
          )}

          {isMobile && location.address && (
            <DescriptionPanel
              address={location.address}
              nearbyPoi={nearbyPoi}
              airport={airport}
              onTabClick={onTabClick}
              onPoiMarkerClick={onPoiMarkerClick}
              transportWithoutAirport={transportWithoutAirport}
              distanceText={distanceText}
              entity={entity}
              hotelId={hotelId}
            />
          )}
        </>
      )}
    </BpkBreakpoint>
  );
};

export default withPoiDataState(
  withMiniEventTracker(withI18n(DetailedLocation)),
);
